import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowsLeftRight, Package, Warehouse, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const LOC_TONE = { zentral: "blue", fahrzeug: "amber", person: "green" };

export default function Lager() {
  const [stock, setStock] = useState(null);
  const [locations, setLocations] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [movements, setMovements] = useState([]);
  const [reorder, setReorder] = useState([]);
  const [open, setOpen] = useState(false);
  const [mv, setMv] = useState({ type: "received", material_id: "", from_location: "", to_location: "", quantity: 1, ref: "" });

  const load = useCallback(() => {
    api.get("/stock").then((r) => setStock(r.data));
    api.get("/stock/locations").then((r) => setLocations(r.data));
    api.get("/materials").then((r) => setMaterials(r.data));
    api.get("/movements").then((r) => setMovements(r.data));
    api.get("/reorder-suggestions").then((r) => setReorder(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const book = async () => {
    try {
      await api.post("/movements", { ...mv, quantity: Number(mv.quantity) || 1, from_location: mv.from_location || null, to_location: mv.to_location || null });
      toast.success("Bewegung gebucht"); setOpen(false); setMv({ type: "received", material_id: "", from_location: "", to_location: "", quantity: 1, ref: "" }); load();
    } catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };

  if (!stock) return <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)}</div>;
  const needsFrom = ["consumed", "scrapped", "transferred"].includes(mv.type);
  const needsTo = ["received", "returned", "transferred"].includes(mv.type);

  return (
    <div>
      <PageHeader title="Lager" subtitle="Zentral-Spiegel · Fahrzeug/Person · Bewegungen · Nachschub">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-movement-button" className="gap-2"><ArrowsLeftRight size={18} weight="bold" />Bewegung buchen</Button></DialogTrigger>
          <DialogContent data-testid="movement-dialog"><DialogHeader><DialogTitle className="font-heading">Lagerbewegung</DialogTitle></DialogHeader>
            <DialogDescription>Wareneingang, Verbrauch, Umlagerung oder Rückgabe.</DialogDescription>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label2>Art</Label2>
                <Select value={mv.type} onValueChange={(v) => setMv((s) => ({ ...s, type: v }))}>
                  <SelectTrigger data-testid="movement-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="received">Wareneingang</SelectItem>
                    <SelectItem value="consumed">Verbrauch</SelectItem>
                    <SelectItem value="transferred">Umlagerung</SelectItem>
                    <SelectItem value="returned">Rückgabe</SelectItem>
                    <SelectItem value="scrapped">Verschrottung</SelectItem>
                  </SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label2>Material</Label2>
                <Select value={mv.material_id} onValueChange={(v) => setMv((s) => ({ ...s, material_id: v }))}>
                  <SelectTrigger data-testid="movement-material"><SelectValue placeholder="Material wählen" /></SelectTrigger>
                  <SelectContent>{materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select></div>
              {needsFrom && <div className="space-y-1.5"><Label2>Von Lagerort</Label2>
                <Select value={mv.from_location} onValueChange={(v) => setMv((s) => ({ ...s, from_location: v }))}>
                  <SelectTrigger><SelectValue placeholder="Quelle" /></SelectTrigger>
                  <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select></div>}
              {needsTo && <div className="space-y-1.5"><Label2>Nach Lagerort</Label2>
                <Select value={mv.to_location} onValueChange={(v) => setMv((s) => ({ ...s, to_location: v }))}>
                  <SelectTrigger><SelectValue placeholder="Ziel" /></SelectTrigger>
                  <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select></div>}
              <div className="space-y-1.5"><Label2>Menge</Label2><Input type="number" data-testid="movement-qty" value={mv.quantity} onChange={(e) => setMv((s) => ({ ...s, quantity: e.target.value }))} /></div>
            </div>
            <DialogFooter><Button data-testid="movement-save" onClick={book} disabled={!mv.material_id}>Buchen</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <Tabs defaultValue="stock">
        <TabsList data-testid="lager-tabs">
          <TabsTrigger value="stock" className="gap-1.5"><Warehouse size={16} />Bestand</TabsTrigger>
          <TabsTrigger value="movements" className="gap-1.5"><ArrowsLeftRight size={16} />Bewegungen</TabsTrigger>
          <TabsTrigger value="reorder" className="gap-1.5"><WarningCircle size={16} />Nachschub {reorder.length > 0 && `(${reorder.length})`}</TabsTrigger>
        </TabsList>

        <TabsContent value="stock">
          <Card className="divide-y divide-border rounded-md border">
            {stock.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Kein Bestand.</div>}
            {stock.map((s) => (
              <div key={s.id} data-testid="stock-row" className="flex items-center justify-between gap-3 px-5 py-3.5">
                <div className="flex min-w-0 items-center gap-3">
                  <Package size={18} weight="duotone" className="shrink-0 text-primary" />
                  <div className="min-w-0"><div className="truncate font-semibold">{s.material_name}</div>
                    <div className="truncate font-mono text-xs text-muted-foreground">{s.material_sku} · {s.location_name}</div></div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge tone={LOC_TONE[s.location_type]}>{s.location_type}</StatusBadge>
                  <span className={`font-heading text-lg font-black ${s.quantity <= s.min_qty ? "text-danger" : ""}`}>{s.quantity}</span>
                  <span className="text-xs text-muted-foreground">{s.unit}</span>
                </div>
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="movements">
          <Card className="divide-y divide-border rounded-md border">
            {movements.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Keine Bewegungen.</div>}
            {movements.map((m) => (
              <div key={m.id} className="flex items-center justify-between px-5 py-3">
                <div><span className="font-semibold">{m.material_name}</span> <span className="text-xs text-muted-foreground">· {m.actor} · {new Date(m.ts).toLocaleString("de-DE")}</span></div>
                <div className="flex items-center gap-2"><StatusBadge tone="blue">{m.type}</StatusBadge><span className="font-mono">{m.quantity}</span></div>
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="reorder">
          <Card className="divide-y divide-border rounded-md border">
            {reorder.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Alle Bestände über Mindestmenge.</div>}
            {reorder.map((r) => (
              <div key={r.id} data-testid="reorder-row" className="flex items-center justify-between px-5 py-3.5">
                <div><div className="font-semibold">{r.material_name}</div><div className="text-xs text-muted-foreground">{r.location_name} · Bestand {r.quantity} / Min {r.min_qty}</div></div>
                <StatusBadge tone="amber">Nachbestellen: {r.suggested_qty}</StatusBadge>
              </div>
            ))}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
